"use client";

import dynamic from "next/dynamic";
import {
  ArrowLeft,
  Check,
  ChevronRight,
  Clock3,
  Coffee,
  Gift,
  Home,
  Info,
  MapPin,
  Minus,
  Plus,
  ReceiptText,
  ShoppingBag,
  Star,
  UserRound,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  categories,
  popularIds,
  products,
  type Product,
} from "@/data/catalog";

import {
  defaultPickupPoint,
  pickupPoints,
  type PickupPoint,
} from "@/data/points";

const PickupMap = dynamic(
  () => import("@/components/PickupMap"),
  {
    ssr: false,
    loading: () => (
      <div className="h-[330px] animate-pulse rounded-[24px] bg-[#e9e3dc]" />
    ),
  },
);

type AppTab = "menu" | "points" | "orders" | "bonuses";

type CartAddon = {
  id: string;
  label: string;
  price: number;
};

type CartLine = {
  key: string;
  productId: string;
  name: string;
  image: string;
  sizeLabel: string;
  addons: CartAddon[];
  unitPrice: number;
  qty: number;
};

type CompactOrder = {
  id: string;
  number: string;
  total: number;
  status: string;
};

const money = (value: number) =>
  new Intl.NumberFormat("ru-RU").format(value) + " ₽";

export default function HomePage() {
  const [tab, setTab] = useState<AppTab>("menu");
  const [category, setCategory] = useState("popular");

  const [pickupPoint, setPickupPoint] =
    useState<PickupPoint>(defaultPickupPoint);

  const [selectedProduct, setSelectedProduct] =
    useState<Product | null>(null);

  const [selectedSizeId, setSelectedSizeId] = useState("");
  const [selectedAddonIds, setSelectedAddonIds] =
    useState<string[]>([]);

  const [productQty, setProductQty] = useState(1);

  const [cart, setCart] = useState<CartLine[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const [placingOrder, setPlacingOrder] = useState(false);

  const [currentOrder, setCurrentOrder] =
    useState<CompactOrder | null>(null);

  const [orderSuccessOpen, setOrderSuccessOpen] =
    useState(false);

  useEffect(() => {
    try {
      const savedPointId = localStorage.getItem(
        "coffeetrack:pickup-point",
      );

      const savedPoint = pickupPoints.find(
        (point) =>
          point.id === savedPointId && point.active,
      );

      if (savedPoint) {
        setPickupPoint(savedPoint);
      }

      const savedOrder = localStorage.getItem(
        "coffeetrack:last-order",
      );

      if (savedOrder) {
        const parsed = JSON.parse(savedOrder);

        if (parsed?.id && parsed?.number) {
          setCurrentOrder(parsed);
        }
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (!currentOrder?.id) return;

    const refresh = async () => {
      try {
        const response = await fetch(
          `/api/orders/${currentOrder.id}`,
          { cache: "no-store" },
        );

        if (!response.ok) return;

        const data = await response.json();

        if (!data.order) return;

        const next = {
          id: data.order.id,
          number: data.order.number,
          total: data.order.total,
          status: data.order.status,
        };

        setCurrentOrder(next);

        try {
          localStorage.setItem(
            "coffeetrack:last-order",
            JSON.stringify(next),
          );
        } catch {}
      } catch {}
    };

    refresh();

    const timer = window.setInterval(refresh, 2500);

    return () => window.clearInterval(timer);
  }, [currentOrder?.id]);

  const visibleProducts = useMemo(() => {
    if (category === "popular") {
      return popularIds
        .map((id) =>
          products.find((product) => product.id === id),
        )
        .filter(
          (product): product is Product => Boolean(product),
        );
    }

    return products.filter(
      (product) => product.category === category,
    );
  }, [category]);

  const cartCount = cart.reduce(
    (sum, line) => sum + line.qty,
    0,
  );

  const cartTotal = cart.reduce(
    (sum, line) => sum + line.unitPrice * line.qty,
    0,
  );

  const selectedSize =
    selectedProduct?.sizes?.find(
      (size) => size.id === selectedSizeId,
    ) ?? null;

  const selectedAddons =
    selectedProduct?.addons?.filter((addon) =>
      selectedAddonIds.includes(addon.id),
    ) ?? [];

  const configuredUnitPrice = selectedProduct
    ? selectedProduct.basePrice +
      (selectedSize?.delta ?? 0) +
      selectedAddons.reduce(
        (sum, addon) => sum + addon.price,
        0,
      )
    : 0;

  const openProduct = (product: Product) => {
    setSelectedProduct(product);
    setSelectedSizeId(product.sizes?.[0]?.id ?? "base");
    setSelectedAddonIds([]);
    setProductQty(1);
  };

  const toggleAddon = (id: string) => {
    setSelectedAddonIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id],
    );
  };

  const addProduct = () => {
    if (!selectedProduct) return;

    const sizeId = selectedSize?.id ?? "base";
    const addonKey = [...selectedAddonIds]
      .sort()
      .join(",");

    const key =
      `${selectedProduct.id}|${sizeId}|${addonKey}`;

    setCart((current) => {
      const existing = current.find(
        (line) => line.key === key,
      );

      if (existing) {
        return current.map((line) =>
          line.key === key
            ? {
                ...line,
                qty: line.qty + productQty,
              }
            : line,
        );
      }

      return [
        ...current,
        {
          key,
          productId: selectedProduct.id,
          name: selectedProduct.name,
          image: selectedProduct.image,
          sizeLabel: selectedSize?.label ?? "",
          addons: selectedAddons,
          unitPrice: configuredUnitPrice,
          qty: productQty,
        },
      ];
    });

    setSelectedProduct(null);
  };

  const changeQty = (key: string, delta: number) => {
    setCart((current) =>
      current
        .map((line) =>
          line.key === key
            ? {
                ...line,
                qty: line.qty + delta,
              }
            : line,
        )
        .filter((line) => line.qty > 0),
    );
  };

  const choosePoint = (point: PickupPoint) => {
    setPickupPoint(point);

    try {
      localStorage.setItem(
        "coffeetrack:pickup-point",
        point.id,
      );
    } catch {}
  };

  const placeOrder = async () => {
    if (!cart.length || placingOrder) return;

    setPlacingOrder(true);

    try {
      const response = await fetch("/api/orders", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          pointId: pickupPoint.id,
          pointName: pickupPoint.name,
          items: cart.map((line) => ({
            name: line.name,
            qty: line.qty,
            unitPrice: line.unitPrice,
            sizeLabel:
              line.sizeLabel || undefined,
            addons: line.addons.map(
              (addon) => addon.label,
            ),
          })),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || "Не удалось создать заказ",
        );
      }

      const compact = {
        id: data.order.id,
        number: data.order.number,
        total: data.order.total,
        status: data.order.status,
      };

      setCurrentOrder(compact);

      try {
        localStorage.setItem(
          "coffeetrack:last-order",
          JSON.stringify(compact),
        );
      } catch {}

      setCart([]);
      setCheckoutOpen(false);
      setCartOpen(false);
      setOrderSuccessOpen(true);
    } catch (error) {
      alert(
        error instanceof Error
          ? error.message
          : "Ошибка заказа",
      );
    } finally {
      setPlacingOrder(false);
    }
  };

  const statusLabel =
    currentOrder?.status === "paid"
      ? "Ждёт принятия"
      : currentOrder?.status === "accepted"
        ? "Готовится"
        : currentOrder?.status === "ready"
          ? "Можно забирать"
          : currentOrder?.status === "completed"
            ? "Выдан"
            : currentOrder?.status === "cancelled"
              ? "Отменён"
              : "Нет заказа";

  const statusTitle =
    currentOrder?.status === "ready"
      ? "Заказ готов!"
      : currentOrder?.status === "accepted"
        ? "Бариста готовит заказ"
        : currentOrder?.status === "completed"
          ? "Спасибо!"
          : "Заказ передан на точку";

  return (
    <main className="min-h-screen bg-[#ebe7e1] text-[#211914]">
      <div className="relative mx-auto min-h-screen max-w-[520px] overflow-hidden bg-[#faf8f5] shadow-[0_0_45px_rgba(30,20,14,.08)]">

        {tab === "menu" ? (
          <>
            <header className="bg-[#211914] px-4 pb-4 pt-[max(16px,env(safe-area-inset-top))] text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-[14px] bg-[#f7b733] text-sm font-black text-[#211914]">
                    CT
                  </span>

                  <div>
                    <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-white/45">
                      CoffeeTrack
                    </div>

                    <div className="text-[17px] font-black leading-tight">
                      Закажи. Забери.
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setTab("bonuses")}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10"
                >
                  <UserRound size={19} />
                </button>
              </div>

              <button
                onClick={() => setTab("points")}
                className="mt-4 flex w-full items-center gap-3 rounded-[18px] bg-white px-3.5 py-3 text-left text-[#211914]"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-[13px] bg-[#fff1c3]">
                  <MapPin size={19} />
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block text-[10px] font-semibold text-[#8e8279]">
                    Забрать заказ
                  </span>

                  <span className="block truncate text-[13px] font-black">
                    {pickupPoint.shortName}
                  </span>
                </span>

                <span className="text-[11px] font-black">
                  {pickupPoint.eta}
                </span>

                <ChevronRight size={17} />
              </button>
            </header>

            <section className="px-4 pt-4">
              <div className="relative overflow-hidden rounded-[24px] bg-[#f7b733] px-5 py-5">
                <div className="relative z-10 max-w-[76%]">
                  <div className="flex items-center gap-1.5 text-[11px] font-black">
                    <Clock3 size={14} />
                    Без очереди
                  </div>

                  <h1 className="mt-2 text-[25px] font-black leading-[1.02] tracking-[-0.045em]">
                    Кофе будет ждать тебя
                  </h1>

                  <p className="mt-2 text-[12px] font-medium leading-4 text-[#604820]">
                    Собери заказ сейчас и просто забери готовым.
                  </p>
                </div>

                <Coffee
                  className="absolute -bottom-7 -right-4 rotate-[-10deg] opacity-[.16]"
                  size={120}
                />
              </div>
            </section>

            <div className="sticky top-0 z-30 mt-4 border-b border-[#e9e3dc] bg-[#faf8f5]/95 backdrop-blur-xl">
              <div className="scrollbar-none flex gap-2 overflow-x-auto px-4 py-3">
                {categories.map((item) => {
                  const active = category === item.id;

                  return (
                    <button
                      key={item.id}
                      onClick={() =>
                        setCategory(item.id)
                      }
                      className={[
                        "shrink-0 rounded-full px-4 py-2.5 text-[12px] font-black",
                        active
                          ? "bg-[#211914] text-white"
                          : "bg-white text-[#645a53] shadow-sm",
                      ].join(" ")}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <section className="px-4 pb-44 pt-5">
              <div className="mb-4 flex items-end justify-between">
                <div>
                  <div className="text-[9px] font-black uppercase tracking-[0.2em] text-[#9a8e85]">
                    CoffeeTrack
                  </div>

                  <h2 className="mt-1 text-[23px] font-black tracking-[-0.035em]">
                    {
                      categories.find(
                        (item) =>
                          item.id === category,
                      )?.label
                    }
                  </h2>
                </div>

                <span className="text-[10px] font-bold text-[#9a8e85]">
                  {visibleProducts.length} поз.
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {visibleProducts.map((product) => (
                  <button
                    key={product.id}
                    onClick={() =>
                      openProduct(product)
                    }
                    className="overflow-hidden rounded-[22px] bg-white text-left shadow-[0_7px_20px_rgba(50,37,28,.06)] active:scale-[.985]"
                  >
                    <div className="relative aspect-square overflow-hidden bg-[#f1ece6]">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="h-full w-full object-cover"
                      />

                      {product.badge ? (
                        <span className="absolute left-2.5 top-2.5 rounded-full bg-[#f7b733] px-2.5 py-1 text-[9px] font-black">
                          {product.badge}
                        </span>
                      ) : null}

                      <span className="absolute bottom-2.5 right-2.5 flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-lg">
                        <Plus
                          size={18}
                          strokeWidth={2.8}
                        />
                      </span>
                    </div>

                    <div className="p-3.5">
                      <h3 className="line-clamp-2 min-h-[34px] text-[14px] font-black leading-[17px]">
                        {product.name}
                      </h3>

                      <p className="mt-1 line-clamp-2 min-h-[30px] text-[10px] leading-[15px] text-[#8a7e76]">
                        {product.description}
                      </p>

                      <div className="mt-3 text-[14px] font-black">
                        от {money(product.basePrice)}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          </>
        ) : null}

        {tab === "points" ? (
          <section className="min-h-screen pb-24">
            <header className="flex items-center gap-3 bg-[#211914] px-4 pb-4 pt-[max(15px,env(safe-area-inset-top))] text-white">
              <button
                onClick={() => setTab("menu")}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10"
              >
                <ArrowLeft size={19} />
              </button>

              <div>
                <div className="text-[9px] font-bold uppercase tracking-[.18em] text-white/45">
                  CoffeeTrack
                </div>

                <h1 className="text-[20px] font-black">
                  Выберите точку
                </h1>
              </div>
            </header>

            <div className="p-4">
              <PickupMap
                points={pickupPoints}
                selectedPointId={pickupPoint.id}
                onSelect={choosePoint}
                heightClass="h-[330px]"
              />

              <div className="mt-4 space-y-3">
                {pickupPoints
                  .filter((point) => point.active)
                  .map((point) => {
                    const selected =
                      point.id === pickupPoint.id;

                    return (
                      <button
                        key={point.id}
                        onClick={() =>
                          choosePoint(point)
                        }
                        className={[
                          "w-full rounded-[22px] border-2 bg-white p-4 text-left",
                          selected
                            ? "border-[#211914]"
                            : "border-transparent",
                        ].join(" ")}
                      >
                        <div className="flex items-start gap-3">
                          <span
                            className={[
                              "flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px]",
                              selected
                                ? "bg-[#f7b733]"
                                : "bg-[#f1ece6]",
                            ].join(" ")}
                          >
                            <MapPin size={20} />
                          </span>

                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <h3 className="text-[14px] font-black">
                                  {point.shortName}
                                </h3>

                                <p className="mt-1 text-[11px] text-[#847870]">
                                  {point.address}
                                </p>
                              </div>

                              {selected ? (
                                <span className="rounded-full bg-[#211914] px-2.5 py-1.5 text-[9px] font-black text-white">
                                  Выбрано
                                </span>
                              ) : null}
                            </div>

                            <div className="mt-3 flex flex-wrap gap-2 text-[10px] font-bold">
                              <span className="rounded-full bg-[#f3eee8] px-2.5 py-1.5">
                                {point.hours}
                              </span>

                              <span className="rounded-full bg-[#f3eee8] px-2.5 py-1.5">
                                {point.eta}
                              </span>

                              {point.rating ? (
                                <span className="rounded-full bg-[#f3eee8] px-2.5 py-1.5">
                                  ★ {point.rating}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
              </div>

              <button
                onClick={() => setTab("menu")}
                className="mt-4 flex w-full items-center justify-between rounded-[18px] bg-[#f7b733] px-5 py-4 text-sm font-black"
              >
                <span>Заберу здесь</span>
                <ChevronRight size={18} />
              </button>
            </div>
          </section>
        ) : null}

        {tab === "orders" ? (
          <section className="min-h-screen pb-24">
            <header className="bg-[#211914] px-4 pb-5 pt-[max(18px,env(safe-area-inset-top))] text-white">
              <div className="text-[9px] font-bold uppercase tracking-[.18em] text-white/45">
                CoffeeTrack
              </div>
              <h1 className="mt-1 text-[23px] font-black">
                Мои заказы
              </h1>
            </header>

            <div className="p-4">
              {currentOrder ? (
                <div className="rounded-[26px] bg-white p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-[.18em] text-[#978b83]">
                        Текущий заказ
                      </div>

                      <div className="mt-2 text-[46px] font-black leading-none tracking-[-.055em]">
                        {currentOrder.number}
                      </div>
                    </div>

                    <span className="rounded-full bg-[#fff0bd] px-3 py-2 text-[10px] font-black">
                      {statusLabel}
                    </span>
                  </div>

                  <div className="mt-5 rounded-[20px] bg-[#f6f2ed] p-4">
                    <div className="flex items-center gap-3">
                      <MapPin size={18} />

                      <div>
                        <div className="text-[10px] text-[#8c8078]">
                          Забрать
                        </div>

                        <div className="text-[13px] font-black">
                          {pickupPoint.shortName}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-[13px] font-bold text-[#766b64]">
                      Сумма
                    </span>

                    <span className="text-[20px] font-black">
                      {money(currentOrder.total)}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center rounded-[26px] bg-white px-5 py-12 text-center">
                  <ShoppingBag size={34} />

                  <h2 className="mt-4 text-[18px] font-black">
                    Заказов пока нет
                  </h2>

                  <p className="mt-2 max-w-[260px] text-[12px] leading-5 text-[#8c8078]">
                    Собери первый заказ — здесь появится его номер и статус.
                  </p>

                  <button
                    onClick={() => setTab("menu")}
                    className="mt-5 rounded-full bg-[#211914] px-5 py-3 text-[12px] font-black text-white"
                  >
                    Открыть меню
                  </button>
                </div>
              )}
            </div>
          </section>
        ) : null}

        {tab === "bonuses" ? (
          <section className="min-h-screen pb-24">
            <header className="bg-[#211914] px-4 pb-5 pt-[max(18px,env(safe-area-inset-top))] text-white">
              <div className="text-[9px] font-bold uppercase tracking-[.18em] text-white/45">
                CoffeeTrack
              </div>

              <h1 className="mt-1 text-[23px] font-black">
                Бонусы
              </h1>
            </header>

            <div className="p-4">
              <div className="relative overflow-hidden rounded-[26px] bg-[#f7b733] p-5">
                <Gift size={28} />

                <h2 className="mt-5 text-[24px] font-black leading-[1.05] tracking-[-.04em]">
                  Каждый заказ становится выгоднее
                </h2>

                <p className="mt-3 text-[12px] leading-5 text-[#604820]">
                  Баллы, персональные акции и бесплатный кофе после регистрации.
                </p>
              </div>

              <div className="mt-4 rounded-[24px] bg-white p-5">
                <div className="text-[10px] font-black uppercase tracking-[.18em] text-[#998d85]">
                  План программы
                </div>

                <div className="mt-4 space-y-4">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-[13px] bg-[#fff1c3]">
                      6
                    </span>

                    <div>
                      <div className="text-[13px] font-black">
                        6-й кофе в подарок
                      </div>
                      <div className="text-[11px] text-[#8b7f77]">
                        Счётчик будет работать автоматически
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-[13px] bg-[#f2ede7]">
                      %
                    </span>

                    <div>
                      <div className="text-[13px] font-black">
                        Персональные акции
                      </div>
                      <div className="text-[11px] text-[#8b7f77]">
                        Для зарегистрированных гостей
                      </div>
                    </div>
                  </div>
                </div>

                <button className="mt-5 w-full rounded-[18px] bg-[#211914] px-5 py-4 text-[13px] font-black text-white">
                  Войти по номеру телефона
                </button>

                <p className="mt-3 text-center text-[10px] text-[#968a82]">
                  Для заказа регистрация не обязательна.
                </p>
              </div>
            </div>
          </section>
        ) : null}

        {cartCount > 0 && tab === "menu" ? (
          <div className="fixed bottom-[72px] left-0 right-0 z-40 px-3">
            <div className="mx-auto max-w-[496px]">
              <button
                onClick={() => setCartOpen(true)}
                className="flex w-full items-center justify-between rounded-[20px] bg-[#f7b733] px-4 py-3.5 shadow-[0_12px_35px_rgba(55,38,10,.2)]"
              >
                <span className="flex items-center gap-3">
                  <span className="flex h-9 min-w-9 items-center justify-center rounded-full bg-[#211914] px-2 text-[13px] font-black text-white">
                    {cartCount}
                  </span>

                  <span className="text-left">
                    <span className="block text-[10px] font-bold opacity-60">
                      Корзина
                    </span>

                    <span className="block text-[13px] font-black">
                      {money(cartTotal)}
                    </span>
                  </span>
                </span>

                <span className="text-[12px] font-black">
                  Открыть →
                </span>
              </button>
            </div>
          </div>
        ) : null}

        <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-[#e5dfd8] bg-white/96 pb-[max(7px,env(safe-area-inset-bottom))] backdrop-blur-xl">
          <div className="mx-auto grid max-w-[520px] grid-cols-4">
            {[
              {
                id: "menu" as const,
                label: "Меню",
                icon: Home,
              },
              {
                id: "points" as const,
                label: "Точки",
                icon: MapPin,
              },
              {
                id: "orders" as const,
                label: "Заказы",
                icon: ReceiptText,
              },
              {
                id: "bonuses" as const,
                label: "Бонусы",
                icon: Gift,
              },
            ].map((item) => {
              const active = tab === item.id;
              const Icon = item.icon;

              return (
                <button
                  key={item.id}
                  onClick={() => setTab(item.id)}
                  className={[
                    "flex flex-col items-center gap-1 py-2",
                    active
                      ? "text-[#211914]"
                      : "text-[#9a8e86]",
                  ].join(" ")}
                >
                  <Icon
                    size={20}
                    strokeWidth={active ? 2.5 : 2}
                  />

                  <span className="text-[9px] font-black">
                    {item.label}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>

        {selectedProduct ? (
          <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/50 backdrop-blur-[2px]">
            <div className="max-h-[94dvh] w-full max-w-[520px] overflow-y-auto rounded-t-[30px] bg-[#faf8f5]">
              <div className="relative aspect-[1.22] bg-[#f0ebe5]">
                <img
                  src={selectedProduct.image}
                  alt={selectedProduct.name}
                  className="h-full w-full object-cover"
                />

                <button
                  onClick={() => setSelectedProduct(null)}
                  className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="px-5 pb-[max(24px,env(safe-area-inset-bottom))] pt-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-[25px] font-black tracking-[-.04em]">
                      {selectedProduct.name}
                    </h2>

                    <p className="mt-1 text-[12px] leading-5 text-[#857970]">
                      {selectedProduct.description}
                    </p>
                  </div>

                  <div className="shrink-0 text-[17px] font-black">
                    {money(configuredUnitPrice)}
                  </div>
                </div>

                {selectedProduct.sizes?.length ? (
                  <div className="mt-6">
                    <h3 className="text-[13px] font-black">
                      Размер
                    </h3>

                    <div className="mt-3 grid grid-cols-2 gap-2">
                      {selectedProduct.sizes.map((size) => {
                        const active =
                          selectedSizeId === size.id;

                        return (
                          <button
                            key={size.id}
                            onClick={() =>
                              setSelectedSizeId(size.id)
                            }
                            className={[
                              "rounded-[16px] border px-4 py-3 text-left",
                              active
                                ? "border-[#211914] bg-[#211914] text-white"
                                : "border-[#ded6cf] bg-white",
                            ].join(" ")}
                          >
                            <div className="text-[12px] font-black">
                              {size.label}
                            </div>

                            <div
                              className={[
                                "mt-1 text-[10px]",
                                active
                                  ? "text-white/55"
                                  : "text-[#958981]",
                              ].join(" ")}
                            >
                              {size.delta > 0
                                ? `+ ${money(size.delta)}`
                                : money(
                                    selectedProduct.basePrice,
                                  )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                {selectedProduct.addons?.length ? (
                  <div className="mt-6">
                    <h3 className="text-[13px] font-black">
                      Добавить
                    </h3>

                    <div className="mt-2 overflow-hidden rounded-[18px] bg-white">
                      {selectedProduct.addons.map(
                        (addon, index) => {
                          const active =
                            selectedAddonIds.includes(
                              addon.id,
                            );

                          return (
                            <button
                              key={addon.id}
                              onClick={() =>
                                toggleAddon(addon.id)
                              }
                              className={[
                                "flex w-full items-center gap-3 px-4 py-3.5 text-left",
                                index
                                  ? "border-t border-[#eee8e2]"
                                  : "",
                              ].join(" ")}
                            >
                              <span
                                className={[
                                  "flex h-6 w-6 items-center justify-center rounded-lg border",
                                  active
                                    ? "border-[#211914] bg-[#211914] text-white"
                                    : "border-[#d8d0c8]",
                                ].join(" ")}
                              >
                                {active ? (
                                  <Check size={14} />
                                ) : null}
                              </span>

                              <span className="flex-1 text-[12px] font-bold">
                                {addon.label}
                              </span>

                              <span className="text-[12px] font-black">
                                +{money(addon.price)}
                              </span>
                            </button>
                          );
                        },
                      )}
                    </div>
                  </div>
                ) : null}

                <button className="mt-5 flex w-full items-center justify-between rounded-[18px] bg-white px-4 py-4 text-left">
                  <span className="flex items-center gap-3 text-[12px] font-black">
                    <Info size={17} />
                    О продукте
                  </span>

                  <ChevronRight size={17} />
                </button>

                <div className="mt-5 flex items-center gap-3">
                  <div className="flex items-center rounded-[18px] bg-white p-1">
                    <button
                      onClick={() =>
                        setProductQty((value) =>
                          Math.max(1, value - 1),
                        )
                      }
                      className="flex h-11 w-11 items-center justify-center"
                    >
                      <Minus size={17} />
                    </button>

                    <span className="w-8 text-center text-[13px] font-black">
                      {productQty}
                    </span>

                    <button
                      onClick={() =>
                        setProductQty(
                          (value) => value + 1,
                        )
                      }
                      className="flex h-11 w-11 items-center justify-center"
                    >
                      <Plus size={17} />
                    </button>
                  </div>

                  <button
                    onClick={addProduct}
                    className="flex h-[52px] flex-1 items-center justify-between rounded-[18px] bg-[#f7b733] px-5 text-[13px] font-black"
                  >
                    <span>Добавить</span>

                    <span>
                      {money(
                        configuredUnitPrice *
                          productQty,
                      )}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {cartOpen ? (
          <div className="fixed inset-0 z-[85] flex justify-center bg-[#faf8f5]">
            <div className="flex min-h-screen w-full max-w-[520px] flex-col">
              <header className="flex items-center justify-between bg-[#211914] px-4 pb-4 pt-[max(16px,env(safe-area-inset-top))] text-white">
                <button
                  onClick={() => setCartOpen(false)}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10"
                >
                  <ArrowLeft size={19} />
                </button>

                <h2 className="text-[18px] font-black">
                  Корзина
                </h2>

                <button
                  onClick={() => setCart([])}
                  className="text-[11px] font-bold text-white/55"
                >
                  Очистить
                </button>
              </header>

              <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-28 pt-4">
                <button
                  onClick={() => {
                    setCartOpen(false);
                    setTab("points");
                  }}
                  className="flex w-full items-center justify-between rounded-[20px] bg-white p-4 text-left"
                >
                  <div>
                    <div className="text-[10px] font-black text-[#968a82]">
                      Забрать в
                    </div>

                    <div className="mt-1 text-[14px] font-black">
                      {pickupPoint.shortName}
                    </div>
                  </div>

                  <ChevronRight size={18} />
                </button>

                <div className="mt-3 space-y-3">
                  {cart.map((line) => (
                    <div
                      key={line.key}
                      className="flex gap-3 rounded-[22px] bg-white p-3"
                    >
                      <img
                        src={line.image}
                        alt={line.name}
                        className="h-24 w-24 rounded-[17px] object-cover"
                      />

                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className="text-[13px] font-black">
                              {line.name}
                            </h3>

                            {line.sizeLabel ? (
                              <div className="mt-1 text-[10px] text-[#8d8179]">
                                {line.sizeLabel}
                              </div>
                            ) : null}

                            {line.addons.length ? (
                              <div className="mt-1 line-clamp-2 text-[10px] text-[#8d8179]">
                                {line.addons
                                  .map(
                                    (addon) =>
                                      addon.label,
                                  )
                                  .join(", ")}
                              </div>
                            ) : null}
                          </div>

                          <div className="whitespace-nowrap text-[13px] font-black">
                            {money(
                              line.unitPrice *
                                line.qty,
                            )}
                          </div>
                        </div>

                        <div className="mt-3 flex items-center gap-2">
                          <button
                            onClick={() =>
                              changeQty(line.key, -1)
                            }
                            className="flex h-8 w-8 items-center justify-center rounded-full bg-[#f1ece6]"
                          >
                            <Minus size={14} />
                          </button>

                          <span className="w-6 text-center text-[12px] font-black">
                            {line.qty}
                          </span>

                          <button
                            onClick={() =>
                              changeQty(line.key, 1)
                            }
                            className="flex h-8 w-8 items-center justify-center rounded-full bg-[#211914] text-white"
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 rounded-[22px] bg-white p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] font-bold text-[#736860]">
                      Сумма заказа
                    </span>

                    <span className="text-[19px] font-black">
                      {money(cartTotal)}
                    </span>
                  </div>

                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-[12px] text-[#8c8078]">
                      Начислим после регистрации
                    </span>

                    <span className="text-[12px] font-black text-[#b57a15]">
                      + бонусы
                    </span>
                  </div>
                </div>
              </div>

              <div className="fixed bottom-0 left-0 right-0 z-10 border-t border-[#e6e0d9] bg-[#faf8f5] px-4 pb-[max(12px,env(safe-area-inset-bottom))] pt-3">
                <div className="mx-auto max-w-[488px]">
                  <button
                    onClick={() => {
                      setCartOpen(false);
                      setCheckoutOpen(true);
                    }}
                    disabled={!cart.length}
                    className="flex w-full items-center justify-between rounded-[19px] bg-[#f7b733] px-5 py-4 text-[13px] font-black disabled:opacity-50"
                  >
                    <span>Оформить заказ</span>
                    <span>{money(cartTotal)}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {checkoutOpen ? (
          <div className="fixed inset-0 z-[90] flex justify-center bg-[#faf8f5]">
            <div className="flex min-h-screen w-full max-w-[520px] flex-col">
              <header className="flex items-center gap-4 bg-[#211914] px-4 pb-4 pt-[max(16px,env(safe-area-inset-top))] text-white">
                <button
                  onClick={() => {
                    setCheckoutOpen(false);
                    setCartOpen(true);
                  }}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10"
                >
                  <ArrowLeft size={19} />
                </button>

                <h2 className="text-[18px] font-black">
                  Оформление заказа
                </h2>
              </header>

              <div className="flex-1 overflow-y-auto px-4 pb-32 pt-4">
                <h3 className="text-[15px] font-black">
                  Точка выдачи
                </h3>

                <button
                  onClick={() => {
                    setCheckoutOpen(false);
                    setTab("points");
                  }}
                  className="mt-3 flex w-full items-center gap-3 rounded-[22px] bg-white p-4 text-left"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-[14px] bg-[#fff1c3]">
                    <MapPin size={20} />
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-black">
                      {pickupPoint.shortName}
                    </div>

                    <div className="mt-1 text-[10px] text-[#887c74]">
                      {pickupPoint.address}
                    </div>
                  </div>

                  <ChevronRight size={17} />
                </button>

                <h3 className="mt-6 text-[15px] font-black">
                  Получение
                </h3>

                <div className="mt-3 rounded-[22px] border-2 border-[#f7b733] bg-white p-4">
                  <div className="flex items-center gap-3">
                    <ShoppingBag size={20} />

                    <div>
                      <div className="text-[13px] font-black">
                        Заберу с собой
                      </div>

                      <div className="mt-1 text-[10px] text-[#8b7f77]">
                        Назови номер заказа на точке
                      </div>
                    </div>
                  </div>
                </div>

                <h3 className="mt-6 text-[15px] font-black">
                  Оплата
                </h3>

                <div className="mt-3 grid grid-cols-3 gap-2">
                  {[
                    "Карта",
                    "СБП",
                    "SberPay",
                  ].map((label, index) => (
                    <button
                      key={label}
                      className={[
                        "rounded-[18px] border-2 bg-white px-2 py-4 text-[11px] font-black",
                        index === 0
                          ? "border-[#f7b733]"
                          : "border-transparent",
                      ].join(" ")}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                <div className="mt-6 rounded-[22px] bg-white p-4">
                  <div className="flex justify-between">
                    <span className="text-[13px] font-bold">
                      Итого
                    </span>

                    <span className="text-[20px] font-black">
                      {money(cartTotal)}
                    </span>
                  </div>

                  <p className="mt-3 text-[10px] leading-4 text-[#93877f]">
                    Пока работает тестовый режим. Реальный эквайринг подключим после завершения интерфейса.
                  </p>
                </div>
              </div>

              <div className="fixed bottom-0 left-0 right-0 border-t border-[#e6e0d9] bg-[#faf8f5] px-4 pb-[max(12px,env(safe-area-inset-bottom))] pt-3">
                <div className="mx-auto max-w-[488px]">
                  <button
                    onClick={placeOrder}
                    disabled={placingOrder}
                    className="flex w-full items-center justify-between rounded-[19px] bg-[#f7b733] px-5 py-4 text-[13px] font-black disabled:opacity-60"
                  >
                    <span>
                      {placingOrder
                        ? "Создаём заказ…"
                        : "Оплатить тестово"}
                    </span>

                    <span>{money(cartTotal)}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {orderSuccessOpen && currentOrder ? (
          <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/50 backdrop-blur-[3px]">
            <div className="w-full max-w-[520px] rounded-t-[32px] bg-[#faf8f5] px-5 pb-[max(28px,env(safe-area-inset-bottom))] pt-5">
              <div className="mx-auto mb-5 h-1.5 w-12 rounded-full bg-[#d7d0c8]" />

              <div className="flex flex-col items-center text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-[22px] bg-[#e5f5e8] text-[#26763d]">
                  <Coffee size={30} />
                </span>

                <div className="mt-5 text-[10px] font-black uppercase tracking-[.22em] text-[#8c8078]">
                  Заказ принят
                </div>

                <div className="mt-2 text-[58px] font-black leading-none tracking-[-.06em]">
                  {currentOrder.number}
                </div>

                <h2 className="mt-4 text-[19px] font-black">
                  {statusTitle}
                </h2>

                <p className="mt-2 max-w-[310px] text-[12px] leading-5 text-[#847870]">
                  Следи за статусом во вкладке «Заказы». Когда всё будет готово — просто назови номер на точке.
                </p>

                <div className="mt-5 w-full rounded-[20px] bg-white p-4">
                  <div className="flex justify-between text-[12px]">
                    <span className="text-[#897d75]">
                      Статус
                    </span>

                    <span className="font-black">
                      {statusLabel}
                    </span>
                  </div>

                  <div className="mt-3 flex justify-between border-t border-[#eee9e3] pt-3">
                    <span className="font-bold">
                      Сумма
                    </span>

                    <span className="font-black">
                      {money(currentOrder.total)}
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setOrderSuccessOpen(false);
                    setTab("orders");
                  }}
                  className="mt-5 w-full rounded-[18px] bg-[#211914] px-5 py-4 text-[13px] font-black text-white"
                >
                  Открыть заказ
                </button>

                <button
                  onClick={() =>
                    setOrderSuccessOpen(false)
                  }
                  className="mt-2 w-full px-5 py-3 text-[11px] font-bold text-[#81756e]"
                >
                  Вернуться в меню
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </main>
  );
}
